import type {LimitReachedEvent} from '@common/core/capacitor/native-playback-contract';

const limitReachedKinds = [
  'previewLimitReached',
  'listenQuotaReached',
  'nextQuotaReached',
  'previousQuotaReached',
] as const;

const limitTypes = ['preview', 'listen24h', 'nextPerHour', 'prevPerHour'] as const;
const promptTypes = ['createAccount', 'upgradePlan'] as const;
const limitSources = ['guest', 'user', 'none'] as const;

export type PlayerLimitReachedKind = (typeof limitReachedKinds)[number];
export type PlayerLimitType = (typeof limitTypes)[number];
export type PlayerLimitPromptType = (typeof promptTypes)[number];
export type PlayerLimitSource = (typeof limitSources)[number];

export interface PlayerLimitPromptPayload {
  identifier: PlayerLimitReachedKind;
  kind: PlayerLimitReachedKind;
  limitType: PlayerLimitType;
  promptType: PlayerLimitPromptType;
  requiresAccountCreation: boolean;
  isAuthenticated: boolean;
  source: PlayerLimitSource;
  surface?: string;
  titleKey?: string;
  messageKey?: string;
  actionKey?: string;
}

type LimitPromptAction = 'play' | 'next' | 'prev';

export function mapNativeLimitReachedEvent(
  event: LimitReachedEvent | null | undefined,
): PlayerLimitPromptPayload | null {
  if (!event) {
    return null;
  }

  if (
    !isLimitReachedKind(event.identifier) ||
    !isLimitReachedKind(event.kind) ||
    !isLimitType(event.limitType) ||
    !isPromptType(event.promptType) ||
    !isLimitSource(event.source)
  ) {
    return null;
  }

  return {
    identifier: event.identifier,
    kind: event.kind,
    limitType: event.limitType,
    promptType: event.promptType,
    requiresAccountCreation: Boolean(event.requiresAccountCreation),
    isAuthenticated: Boolean(event.isAuthenticated),
    source: event.source,
    surface: normalizeOptionalString(event.surface),
    titleKey: normalizeOptionalString(event.titleKey),
    messageKey: normalizeOptionalString(event.messageKey),
    actionKey: normalizeOptionalString(event.actionKey),
  };
}

export function createLimitPromptSignature(
  payload: PlayerLimitPromptPayload,
): string {
  return [
    payload.identifier,
    payload.kind,
    payload.limitType,
    payload.promptType,
    payload.requiresAccountCreation ? '1' : '0',
    payload.isAuthenticated ? '1' : '0',
    payload.source,
    payload.surface ?? '',
    payload.titleKey ?? '',
    payload.messageKey ?? '',
    payload.actionKey ?? '',
  ].join('|');
}

export function mapLimitTypeToPromptAction(
  limitType: PlayerLimitType,
): LimitPromptAction {
  switch (limitType) {
    case 'nextPerHour':
      return 'next';
    case 'prevPerHour':
      return 'prev';
    case 'preview':
    case 'listen24h':
    default:
      return 'play';
  }
}

function isLimitReachedKind(value: unknown): value is PlayerLimitReachedKind {
  return typeof value === 'string' && (limitReachedKinds as readonly string[]).includes(value);
}

function isLimitType(value: unknown): value is PlayerLimitType {
  return typeof value === 'string' && (limitTypes as readonly string[]).includes(value);
}

function isPromptType(value: unknown): value is PlayerLimitPromptType {
  return typeof value === 'string' && (promptTypes as readonly string[]).includes(value);
}

function isLimitSource(value: unknown): value is PlayerLimitSource {
  return typeof value === 'string' && (limitSources as readonly string[]).includes(value);
}

function normalizeOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}
